import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import { SEO } from '../components/SEO';

export default function About(): React.ReactElement {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About"
        description="Nivoon is a veteran-owned B2B SaaS company founded by career technology consultants and CIOs. Based in Minneapolis, MN, we build intelligent requirements management software for modern teams."
        path="/about"
      />

      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-indigo-950 to-gray-900">
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-indigo-500 rounded-full filter blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-blue-500 rounded-full filter blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
          <p className="text-indigo-400 font-semibold text-sm uppercase tracking-widest mb-4">About Nivoon</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
            Built by People Who've<br />
            <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">
              Been in Your Shoes
            </span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            We've spent decades leading technology teams, managing complex programs, and delivering enterprise software.
            We built Nivoon because we knew there was a better way.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">Our Story</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
            <p>
              Nivoon was founded by career technology consultants and CIOs who have spent their
              professional lives solving complex technology challenges for organizations of every size —
              from early-stage startups finding product-market fit to Fortune 50 enterprises managing
              portfolios of hundreds of concurrent programs.
            </p>
            <p>
              Through that experience, one truth became clear: the gap between what a business needs
              and what actually gets built is where most projects fail. Requirements get lost in
              spreadsheets, scattered across email threads, and buried in documents that nobody reads.
              Approvals stall. Priorities shift without visibility. Teams ship the wrong thing — not
              because they lack talent, but because they lack clarity.
            </p>
            <p>
              We built Nivoon to close that gap. Our platform brings AI-powered intelligence to
              requirements management, giving teams a single source of truth from ideation through
              delivery. Every feature we ship is informed by real-world experience leading technology
              organizations and a deep understanding of what it takes to deliver software that matters.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">What We Believe</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Our values are shaped by decades of building and delivering technology for real organizations.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Clarity Over Complexity',
                desc: 'The best tools make hard things simple, not simple things complicated. We obsess over reducing friction so teams can focus on building great products.',
                icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
              },
              {
                title: 'Practitioners First',
                desc: 'We build for the people doing the work — project managers, engineers, business analysts, and stakeholders. Every design decision starts with their reality.',
                icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
              },
              {
                title: 'Ship and Iterate',
                desc: 'We deploy continuously, listen to customers, and improve relentlessly. Perfection is a direction, not a destination — what matters is that things get better every week.',
                icon: 'M13 10V3L4 14h7v7l9-11h-7z',
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center mb-5">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & VOSB */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Minneapolis */}
            <div>
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-5">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Based in Minneapolis, MN</h2>
              <p className="text-gray-600 leading-relaxed">
                Nivoon is headquartered in Minneapolis, Minnesota — a thriving hub for technology
                and innovation in the Upper Midwest. While our roots are local, our team and our
                customers span the country. We embrace remote work and believe great talent isn't
                limited by geography.
              </p>
            </div>

            {/* Veteran Owned */}
            <div>
              <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center mb-5">
                <svg className="w-6 h-6 text-red-700" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M3 2.25a.75.75 0 01.75.75v.54l1.838-.46a9.75 9.75 0 016.725.738l.108.054a8.25 8.25 0 005.58.652l3.109-.732a.75.75 0 01.917.81 47.784 47.784 0 00.005 10.337.75.75 0 01-.574.812l-3.114.733a9.75 9.75 0 01-6.594-.77l-.108-.054a8.25 8.25 0 00-5.69-.625l-1.81.452A.75.75 0 013 14.175V3a.75.75 0 01.75-.75z" clipRule="evenodd" />
                  <path d="M3 14.175V21.75a.75.75 0 001.5 0v-7.121l-.402.1a.75.75 0 01-1.098-.654z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Veteran-Owned Small Business</h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Nivoon is proud to be a Veteran-Owned Small Business (VOSB). Our founders bring the
                discipline, mission focus, and commitment to excellence that military service instills —
                values that shape how we build our product, serve our customers, and grow our company.
              </p>
              <a
                href="https://www.sba.gov/federal-contracting/contracting-assistance-programs/veteran-owned-small-businesses"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
              >
                Learn more about Veteran-Owned Small Businesses
                <svg className="w-4 h-4 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to see what Nivoon can do?</h2>
          <p className="text-indigo-100 text-lg mb-8 max-w-2xl mx-auto">
            Start your free trial today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-3.5 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors shadow-lg text-lg"
            >
              Get Started Free
            </button>
            <button
              onClick={() => navigate('/features')}
              className="px-8 py-3.5 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors text-lg"
            >
              Explore Features
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="/features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="/pricing" className="hover:text-white transition-colors">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="/about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="/blog" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="/careers" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="/investor-relations" className="hover:text-white transition-colors">Investor Relations</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="/legal" className="hover:text-white transition-colors">Privacy & Terms</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Get Started</h3>
              <ul className="space-y-2">
                <li><a href="/register" className="hover:text-white transition-colors">Sign Up Free</a></li>
                <li><a href="/login" className="hover:text-white transition-colors">Sign In</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} Nivoon. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
